<template>
  <div class="daily-routine-viewer" v-if="showDailyRoutineViewer" :class="{ 'closing': isClosing }">
    <div class="page-wrapper" :style="{ 'height': '100dvh', 'height': '-webkit-fill-available' }">
      <div class="content">
        <div class="close-action" v-on:click="close">
          <img :src="require(`@/assets/icons/close-white.svg`)"/>
        </div>
        <div class="page-content" ref="pageContent">
          <div class="dr-container">
            <div v-for="(dailyRoutine, index) in filteredRoutines"
                 :key="index"
                 :ref="el => cardRefs[index] = el"
                 :style="getCardStyle(index)"
                 class="daily-routine-item"
                 @touchstart.prevent="startDrag($event, index)"
                 @mousedown.prevent="startDrag($event, index)">
              <div class="background" :style="{ background: dailyRoutine.page.content.background }"></div>
              <viewer-widgets v-if="dailyRoutine.page.content.widgets" :widgets="dailyRoutine.page.content.widgets"
                              @userInteraction="onUserInteraction" />
            </div>
          </div>
        </div>
        <div v-if=hasCardActions class="buttons flex-row">
          <riseup-button
              :title="firstAction.text"
              :action="($event) => handleAction(firstAction.action, $event)"
              variant="secondary"
              :style="firstActionStyle"
              class="action-button"/>
          <div class="card-text">נשארו עוד {{dailyRoutines.length}}</div>
          <riseup-button
              v-if="secondAction"
              :title="secondAction.text"
              variant="secondary"
              :action="($event) => handleAction(secondAction.action, $event)"
              :style="secondActionStyle"
              class="action-button"/>
        </div>
        <div v-else class="buttons flex-row">
          <div class="card-text" @click="handleNoAction($event)">{{ firstAction.text }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import storyLib from '@riseupil/story-lib';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import BaseUI from '@riseupil/base-ui';
import TransactionMenu
  // eslint-disable-next-line max-len
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/generic-components/cashflow-types/cashflow-types-base/TransactionMenu.vue';
import TransactionMixin
  // eslint-disable-next-line max-len
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/generic-components/cashflow-types/cashflow-types-base/TransactionMixin.vue';
import ChangeTransactionCategoryPopup
  // eslint-disable-next-line max-len
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/current-month/components/cashflow-edit/edit-transaction/ChangeTransactionCategoryPopup.vue';
import EditAmountPopup
  // eslint-disable-next-line max-len
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/current-month/components/cashflow-edit/EditAmountPopup.vue';
import consts from '@riseupil/story-lib/src/components/consts';
import cashflowViewConsts from '@/constants/cashflow-view';
import Intercom from '@/Intercom';
import Segment from '@/Segment';

export default {
  name: 'DailyRoutineViewer',
  mixins: [TransactionMixin],
  components: {
    ViewerWidgets: storyLib.ViewerWidgets,
    RiseupButton: BaseUI.RiseupButton,
  },
  data() {
    return {
      currentPageIndex: 0,
      containerHeight: 0,
      containerWidth: 0,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      dragging: false,
      activeIndex: null,
      cardRefs: [],
      removedCardIds: [],
      removedCardTypes: [],
      initialTransform: null,
      swipeDirection: null,
      swipeThreshold: 50,
      isClosing: false,
    };
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  watch: {
    showDailyRoutineViewer(val) {
      if (val) {
        this.$nextTick(() => {
          // Intercom.hideLauncher();
          // Intercom.hide();
          this.onResize();
          window.addEventListener('resize', this.onResize);
        });
      } else {
        window.removeEventListener('resize', this.onResize);
      }
    },
  },
  computed: {
    ...mapState('dailyRoutines', ['dailyRoutines', 'showDailyRoutineViewer',
      'specialCards', 'isIntroDismissed']),
    ...mapGetters('cashflowView', ['trackingCategories']),
    ...mapGetters('dailyRoutines', ['doneCard']),
    trackingCategory() {
      const dailyRoutineCategoryId = this.selectedDailyRoutine?.transaction
        ? this.selectedDailyRoutine.transaction?.categoryId : this.selectedDailyRoutine.data?.id;
      return this.trackingCategories.find(category => category.categoryId === dailyRoutineCategoryId);
    },
    transaction() {
      const isSaving = cashflowViewConsts.FIXED_SAVING_CATEGORY_NAMES.includes(this.selectedDailyRoutine.transaction.categoryLabel);
      const { transaction } = this.selectedDailyRoutine;
      return {
        ...transaction,
        categoryName: this.getCategoryName(transaction, isSaving),
        trackingCategory: this.trackingCategory,
        isSaving,
        [transaction?.isIncome ? 'incomeAmount' : 'billingAmount']: transaction?.amount,
      };
    },
    categoryName() {
      return this.transaction?.categoryName;
    },
    categoryId() {
      return this.transaction?.categoryId;
    },
    firstAction() {
      return this.selectedDailyRoutine.actions[0];
    },
    hasCardActions() {
      return this.firstAction.action !== 'none' && this.firstAction.action !== 'more_cards';
    },
    secondAction() {
      return this.selectedDailyRoutine.actions.length > 1 ? this.selectedDailyRoutine.actions[1] : null;
    },
    dailyRoutinesToDisplay() {
      return [
        ...(this.isIntroDismissed ? [] : [this.specialCards.INTRO]),
        ...this.dailyRoutinesIncludingDoneCard,
      ].slice(0, 4);
    },
    dailyRoutinesIncludingDoneCard() {
      return [...this.dailyRoutines, this.doneCard].slice(0, 4);
    },
    cardScale() {
      const cardHeightRatio = this.containerHeight / consts.BASE_SCREEN_HEIGHT;
      const cardWidthRatio = this.containerWidth / consts.BASE_SCREEN_WIDTH;
      console.log('cardHeightRatio', cardHeightRatio, 'cardWidthRatio', cardWidthRatio);
      return Math.min(cardHeightRatio, cardWidthRatio);
    },
    filteredRoutines() {
      return this.dailyRoutinesToDisplay.filter(routine => {
        if (routine?.type === 'INTRO' || routine?.type === 'DONE' || routine?.type === 'DONE_WITH_MORE_CARDS') {
          return !this.removedCardTypes.includes(routine.type);
        }
        return !this.removedCardIds.includes(routine?.entityId);
      });
    },
    selectedDailyRoutine() {
      return this.dailyRoutinesToDisplay[0];
    },
    firstActionStyle() {
      if (!this.dragging) {
        return {};
      }
      return this.swipeDirection === 'right'
        ? { background: BaseUI.Colors.riseupGreen, borderColor: BaseUI.Colors.riseupGreen }
        : {};
    },
    secondActionStyle() {
      if (!this.dragging) {
        return {};
      }
      return this.swipeDirection === 'left'
        ? { background: BaseUI.Colors.riseupDangerRed, borderColor: BaseUI.Colors.riseupDangerRed }
        : {};
    },
  },
  methods: {
    ...mapActions('dailyRoutines', ['dismissDailyRoutine', 'dismissIntro', 'markTasksCompletedForToday', 'fetchDailyRoutinesNextChunk']),
    ...mapMutations('dailyRoutines', ['setShowDailyRoutineViewer']),
    ...mapActions('modalRootStore', ['openModal']),
    ...mapActions('editTransaction', ['startEditingTransaction']),
    getCategoryName(transaction, isSaving) {
      if (isSaving) {
        return transaction.categoryTypeV1 === cashflowViewConsts.CATEGORY_NAMES.FIXED
          ? cashflowViewConsts.CATEGORY_NAMES.FIXED_SAVING
          : cashflowViewConsts.CATEGORY_NAMES.ONE_TIME_SAVING;
      }
      if (transaction.categoryTypeV1 === cashflowViewConsts.CATEGORY_NAMES.VARIABLE) {
        return cashflowViewConsts.CATEGORY_NAMES.VARIABLE;
      }
      if (transaction.categoryTypeV1 === cashflowViewConsts.CATEGORY_NAMES.VARIABLE_INCOME) {
        return cashflowViewConsts.CATEGORY_NAMES.VARIABLE_INCOME;
      }
      if (this.trackingCategory) {
        return this.trackingCategory?.categoryName;
      }
      return '';
    },
    onResize() {
      // const styles = window.getComputedStyle(this.$refs.pageContent);
      // Calculate content height (without padding)
      this.containerHeight = this.$refs.pageContent.clientHeight - 44;

      // Calculate content width (without padding)
      this.containerWidth = this.$refs.pageContent.clientWidth;

      console.log('containerHeight', this.containerHeight, 'containerWidth', this.containerWidth);
    },
    getDailyRoutineTypeToActionPopupMap() {
      const openTransactionBottomMenu = async () => {
        await this.openModal({
          component: TransactionMenu,
          props: {
            actions: this.getActions(),
            categoryName: this.transaction.categoryName,
            transaction: this.transaction,
            isPredictedTransaction: false,
            isTemp: this.transaction.isTemp,
          },
          popupAlignment: 'menu',
          eventHandlers: {
            close: this.onActionDone,
          },
        });
      };
      const openSetCategoryModal = async () => {
        await this.openModal({
          component: EditAmountPopup,
          props: {
            expenseCategory: this.trackingCategory.categoryName,
            category: this.trackingCategory?.categories.length > 1
              ? this.trackingCategory.categories[this.currentWeek.weekIndex]
              : this.trackingCategory.categories[0],
          },
          eventHandlers: {
            close: this.onActionDone,
          },
        });
      };
      const openChangeCategoryModal = async () => {
        await this.startEditingTransaction({
          transaction: this.transaction,
          categoryName: this.transaction.categoryName,
          categoryId: this.transaction.categoryId,
        });
        await this.openModal({ component: ChangeTransactionCategoryPopup });
      };
      return {
        CATEGORY_EXCEEDED: openSetCategoryModal,
        CATEGORY_ABOUT_TO_EXCEED: openSetCategoryModal,
        EXPENSE_EXCLUSION: openTransactionBottomMenu,
        INCOME_EXCLUSION: openTransactionBottomMenu,
        SIGNIFICANT_FIXED_INCOME_HIGHER_THAN_PREVIOUS: openTransactionBottomMenu,
        NEW_TRANSACTION_CATEGORIZATION: openTransactionBottomMenu,
        MIGHT_BE_FIXED: openChangeCategoryModal,
      };
    },

    close() {
      this.isClosing = true;
      setTimeout(() => {
        if (this.dailyRoutines.length === 0) {
          this.markTasksCompletedForToday();
        }
        this.removedCardIds = [];
        this.removedCardTypes = [];
        Intercom.showLauncher();
        this.setShowDailyRoutineViewer(false);
        this.isClosing = false;
      }, 300);
    },

    async onActionDone() {
      Intercom.hideLauncher();
      Intercom.hide();
      await this.dismissDailyRoutine(this.selectedDailyRoutine.entityId);
    },

    async handleAction(type, e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (type === 'dismiss') {
        await this.dismissCardAction();
      } else {
        await this.executeCardAction();
      }
    },

    async executeCardAction() {
      switch (this.selectedDailyRoutine.type) {
      case 'INTRO':
        await this.dismissIntro();
        break;
      case 'DONE':
        await this.close();
        break;
      case 'DONE_WITH_MORE_CARDS':
        await this.close();
        break;
      default:
        await this.showActionPopup();
      }
    },

    async dismissCardAction() {
      switch (this.selectedDailyRoutine.type) {
      case 'INTRO':
        await this.dismissIntro();
        break;
      case 'DONE':
        await this.close();
        break;
      case 'DONE_WITH_MORE_CARDS':
        await this.close();
        break;
      default:
        await this.dismissDailyRoutine(this.selectedDailyRoutine.entityId);
      }
    },

    async handleNoAction(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      switch (this.selectedDailyRoutine.type) {
      case 'INTRO':
        await this.dismissIntro();
        break;
      case 'DONE':
        await this.markTasksCompletedForToday();
        await this.close();
        break;
      case 'DONE_WITH_MORE_CARDS':
        await this.fetchDailyRoutinesNextChunk();
        break;
      default:
        await this.dismissCardAction();
      }
    },

    async showActionPopup() {
      const mapper = this.getDailyRoutineTypeToActionPopupMap();
      const flavour = this.selectedDailyRoutine.flavour ? `_${this.selectedDailyRoutine.flavour}` : '';
      if (mapper[`${this.selectedDailyRoutine.type}${flavour}`]) {
        await mapper[`${this.selectedDailyRoutine.type}${flavour}`]();
      } else {
        console.log('no mapper for action', `${this.selectedDailyRoutine.type}${flavour}`);
      }
    },

    getCardStyle(index) {
      const baseStyle = index === this.activeIndex && this.dragging
        ? this.animatedCardScaleCss(index)
        : this.cardScaleCss(index);

      if (index === this.activeIndex && this.dragging) {
        return {
          ...baseStyle,
          transform: `${baseStyle.transform} ${this.cardTransform}`, // Combine transforms
        };
      }

      return baseStyle;
    },

    cardScaleCss(index) {
      const cardScaleRatio = index === 0 ? 1 : 0.9 ** index;

      let top = 0;
      if (index === 1) {
        top = 16;
      } else if (index === 2) {
        top = 28;
      } else if (index === 3) {
        top = 36;
      }
      const translateX = (this.containerWidth - consts.BASE_SCREEN_WIDTH) / 2;
      const translateY = (((this.containerHeight - consts.BASE_SCREEN_HEIGHT) / cardScaleRatio) / 2);
      return {
        transform: `scale(${cardScaleRatio * this.cardScale}) translate(${translateX}px, ${translateY - top}px)`,
        zIndex: 4 - index,
      };
    },
    animatedCardScaleCss(index) {
      const translateX = (this.containerWidth - consts.BASE_SCREEN_WIDTH) / 2;
      const translateY = (this.containerHeight - consts.BASE_SCREEN_HEIGHT) / 2;
      const dx = this.currentX - this.startX;
      const rotate = dx / 10;

      return {
        transition: this.dragging ? 'none' : 'transform 0.3s ease-out',
        transform: `translate(${translateX}px, ${translateY}px) rotate(${rotate}deg))`,
        zIndex: 4 - index,
      };
    },
    startDrag(e, index) {
      if (index !== 0 || this.dragging) {
        return;
      }

      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      this.activeIndex = index;
      this.dragging = true;

      // Save the initial transform to restore it later
      const cardElement = this.cardRefs[0];
      if (cardElement) {
        const computedStyle = window.getComputedStyle(cardElement);
        this.initialTransform = computedStyle.transform !== 'none' ? computedStyle.transform : '';
      }

      // Handle both mouse and touch events
      const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
      const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);

      this.startX = clientX;
      this.startY = clientY;
      this.currentX = clientX;
      this.currentY = clientY;

      // Set up event listeners for both mouse and touch
      document.addEventListener('mousemove', this.onDrag, { passive: false });
      document.addEventListener('mouseup', this.endDrag);
      document.addEventListener('touchmove', this.onDrag, { passive: false });
      document.addEventListener('touchend', this.endDrag);
    },

    onDrag(e) {
      if (!this.dragging) {
        return;
      }

      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Handle both mouse and touch events
      const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
      this.currentX = clientX;

      // Calculate the drag distance
      const dx = this.currentX - this.startX;

      // Apply the transform directly to the card element
      const cardElement = this.cardRefs[0];
      if (cardElement) {
        const dampedDx = dx * 1.5;
        const rotate = dampedDx / 15;

        if (Math.abs(dx) > this.swipeThreshold) {
          this.swipeDirection = dx > 0 ? 'right' : 'left';
        } else {
          this.swipeDirection = null;
        }

        cardElement.style.transition = 'none';
        cardElement.style.transform = `${this.initialTransform} translate3d(${dampedDx}px, 0, 0) rotate(${rotate}deg)`;
      }
    },

    endDrag(e) {
      if (!this.dragging || this.activeIndex === null) {
        return;
      }

      if (e) {
        if (e.preventDefault) {
          e.preventDefault();
        }
        if (e.stopPropagation) {
          e.stopPropagation();
        }
      }

      const dx = this.currentX - this.startX;
      const dy = this.currentY - this.startY;
      const threshold = 100;

      const isLastCard = this.dailyRoutinesIncludingDoneCard.length === 1;

      if (isLastCard && Math.abs(dy) > threshold) {
        this.animateSwipeDown(this.activeIndex);
      } else if (Math.abs(dx) > threshold) {
        const direction = dx > 0 ? 'right' : 'left';
        this.animateSwipeSide(this.activeIndex, direction);
      } else {
        // If the drag didn't exceed the threshold, reset the card position
        this.resetCardPosition();
      }

      this.swipeDirection = null;
      this.resetDragState();
    },

    resetCardPosition() {
      const cardElement = this.cardRefs[0];
      if (cardElement && this.initialTransform) {
        cardElement.style.transition = 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        cardElement.style.transform = this.initialTransform;
      }
    },

    animateSwipeDown(index) {
      const el = this.cardRefs[index];
      const dailyRoutine = this.dailyRoutinesToDisplay[index];

      // Add card to removed list before animation starts
      if (dailyRoutine?.type === 'INTRO' || dailyRoutine?.type === 'DONE' || dailyRoutine?.type === 'DONE_WITH_MORE_CARDS') {
        this.removedCardTypes.push(dailyRoutine.type);
      } else if (dailyRoutine?.entityId) {
        this.removedCardIds.push(dailyRoutine.entityId);
      }

      el.style.transition = 'transform 0.3s ease-out';
      el.style.transform = 'translateY(100vh)';

      setTimeout(() => {
        this.close();
      }, 300);
    },

    animateSwipeSide(index, direction) {
      const el = this.cardRefs[index];
      const offset = direction === 'right' ? '100vw' : '-100vw';
      const dailyRoutine = this.dailyRoutinesToDisplay[index];

      // Add card to removed list before animation starts
      if (dailyRoutine?.type === 'INTRO' || dailyRoutine?.type === 'DONE' || dailyRoutine?.type === 'DONE_WITH_MORE_CARDS') {
        this.removedCardTypes.push(dailyRoutine.type);
      } else if (dailyRoutine?.entityId) {
        this.removedCardIds.push(dailyRoutine.entityId);
      }

      el.style.transition = 'transform 0.3s ease-out';
      el.style.transform = `translate3d(${offset}, 0, 0)`;

      setTimeout(async () => {
        if (direction === 'left') {
          await this.executeCardAction();
        } else {
          await this.dismissCardAction();
        }
      }, 300);
    },

    resetDragState() {
      this.dragging = false;
      this.currentX = 0;
      this.currentY = 0;
      this.startX = 0;
      this.startY = 0;
      this.activeIndex = null;

      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.endDrag);
      document.removeEventListener('touchmove', this.onDrag);
      document.removeEventListener('touchend', this.endDrag);
    },

    onUserInteraction(data) {
      if (data.eventName === 'fetchMoreCards') {
        Segment.trackUserInteraction('DailyRoutine__fetchMoreCards');
        this.fetchDailyRoutinesNextChunk();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';

.daily-routine-viewer {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  animation: fadeIn 0.3s ease-in-out forwards;

  &.closing {
    animation: fadeOut 0.3s ease-in-out forwards;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }

  .page-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100dvh;
    overscroll-behavior: none;
  }

  @media (min-width: 430px) {
    .page-wrapper {
      width: 400px;
    }
  }

  .content {
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: scroll;
    overflow-x: hidden;
    background: #000000b3;
    overscroll-behavior: none;
    touch-action: none;

    .close-action {
      z-index: 10;
      margin: 20px 16px 28px;
    }

    .buttons {
      align-items: center;
      justify-content: space-between;
      margin: 20px 16px 20px;

      .action-button {
        color: $riseup_white;
        width: 100px;
        border: 1px solid $riseup_white;
      }

      .card-text {
        display: flex;
        flex: 1;
        justify-content: center;
        color: $riseup_white;
      }
    }

    .page-content {
      background: transparent;
      margin: 0 16px;
      padding-top: 90px; // This is done for the 4 cards in stack
      flex-grow: 1;
      position: relative;
    }
  }

  .dr-container {
    position: relative;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: contain;
    overscroll-behavior: none;
    touch-action: none;
    user-select: none;

    .daily-routine-item {
      position: relative;
      cursor: grab;
      opacity: 0;

      // First card (index 0) appears immediately
      &:nth-child(1) {
        animation: fadeIn 0.3s ease-in-out forwards;
      }

      // All other cards appear after 0.3s delay
      &:nth-child(n+2) {
        animation: fadeIn 0.3s ease-in-out 0.3s forwards;
      }

      &:active {
        cursor: grabbing;
      }

      .background {
        position: absolute;
        top: 0;
        left: 0;
        height: 768px;
        width: 430px;
        background: lightgreen;
        border-radius: 24px;
        box-shadow: 0px -4px 11.6px 0px #48233021;
      }
      height: 100%;
    }
  }

}
</style>
